/**
 * resolve-write-target — 运营位「全 PUT 写动作」的并发安全 + App override scope 统一解析
 *
 * adversarial r1/r2 共享 helper：所有非 GET 写路径（行菜单 toggle + 编辑弹窗 + slide 写）都走它，
 * 不再各路径各拼一份 scope 逻辑（r2 发现编辑弹窗 / slide 漏走 → App 视图改全局基线、污染所有 App）。
 *
 *  - F1（并发安全）：从 query cache **实时**取该行最新值（按 id），不用 render 闭包快照——避免另一运营改了
 *    title 后，stale 写把旧 title/排序/白名单全字段恢复（静默数据丢失）。cache 落空（被刷掉）→ 回退传入行。
 *  - F2（App override scope）：App 视图（currentAppId!=='all'）下对全局行（latest.appId===null）的写 = 建
 *    App override：asOverride=true + targetAppId=当前 App，调用方据此发 app_id=当前 App + 复用 section_id/slug
 *    + 省 id（server 侧按 (app_id, section_id|slug) 建 / 改该 App 的 override 行，
 *    不动全局基线）。否则 asOverride=false + targetAppId=latest.appId（按原行 scope 写）。
 */

import type { QueryClient } from '@tanstack/react-query';

export interface WriteTarget<T> {
  /** query cache 实时最新行（按 id 命中），落空回退传入行 */
  row: T;
  /** 目标 scope 的 app_id：override → 当前 App；否则 = 原行 app_id（可为 null=全局） */
  targetAppId: string | null;
  /** true = App 视图下对全局行的写 → 建/路由到 App override（省 id），不改全局基线 */
  asOverride: boolean;
}

/**
 * 解析全 PUT 写动作的目标行 + scope。
 * @param cacheKey 该行所属列表的 query key（如 ['home-blocks', appId] / ['banners', appId]）
 * @param fallbackRow cache 落空时的兜底行（调用方持有的渲染态行）
 * @param currentAppId 当前 App scope（'all' = 聚合视图，无具体 App scope）
 */
export function resolveWriteTarget<T extends { id: string; appId: string | null }>(
  queryClient: QueryClient,
  cacheKey: readonly unknown[],
  fallbackRow: T,
  currentAppId: string,
): WriteTarget<T> {
  const cached = queryClient.getQueryData<T[] | { items: T[] }>(cacheKey);
  const rows = Array.isArray(cached) ? cached : (cached?.items ?? []);
  const latest = rows.find((r) => r.id === fallbackRow.id) ?? fallbackRow;
  // App 视图下全局行（appId===null）→ 建 App override；其余按原行 scope。
  const asOverride = currentAppId !== 'all' && latest.appId === null;
  const targetAppId = asOverride ? currentAppId : latest.appId;
  return { row: latest, targetAppId, asOverride };
}
